import {
  Component,
  OnInit,
  Input,
  ViewContainerRef,
  OnChanges,
  SimpleChanges,
  Optional,
  OnDestroy,
  ViewChild,
  ElementRef,
  EventEmitter,
  Output,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  UntypedFormArray,
} from '@angular/forms';
import { DialogProviderService } from '@app/core/services/dialog-provider.service';
import { TranslateService } from '@ngx-translate/core';
import { ClientService } from '../../../../core/services/client.service';
import { NotificationService } from '../../../../core/services/notification.service';
import { Router } from '@angular/router';
import { Client } from '../../../../core/types/client';
import { ContactService } from '../../../../core/services/contacts.service';
import { CreditService } from '../../../../core/services/credit.service';
import {
  ContactPhone,
  ContactPhoneType,
  ContactPhoneDto,
} from '../../../../core/types/contact-phone';
import { PHONE_TYPES } from '../../../../core/types/contact-phone-types';
import { catchError, takeUntil } from 'rxjs/operators';
import { of, Subject } from 'rxjs';
import { cloneDeep, isEqual } from 'lodash-es';
import { InteractionMode } from '../../../../core/types/interaction-mode';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CompanyPhonesComponent } from './company-phones/company-phones.component';
import { IdentityReportsService } from '../../../../core/services/identity-reports.service';
import { EventLogAddContactDialogComponent } from '@app/main/main-layout/right-drawer/event-log/event-log-add-contact/event-log-add-contact-dialog.component';
import { FormComponentData } from '@app/core/types/form-component-data';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'itfg-contact-data-edit',
  templateUrl: './contact-data-edit.component.html',
  styleUrls: ['./contact-data-edit.component.scss'],
  standalone: false
})
export class ContactDataEditComponent implements OnInit, OnChanges, OnDestroy {
  @Input() client: Client;
  @Input() phoneNumber: string;
  @Input() interactionMode: InteractionMode;
  @Input() inputContacts: ContactPhone[];
  @Input() inputPhoneTypes: ContactPhoneType[];
  @Output() dataChanged = new EventEmitter<FormComponentData>();
  @ViewChild('descriptionInputRef')
  descriptionInputRef: ElementRef<HTMLInputElement>;
  contactForms: UntypedFormArray = new UntypedFormArray([]);

  clientId: number;
  contactList: ContactPhone[];
  contactsPhoneType: ContactPhoneType[];
  saveBtnEnabled: boolean[] = [];
  modes: typeof InteractionMode;
  currDate: Date;
  isInCreditEdit: boolean;
  isInClientEdit: boolean;
  prevClientMobile: string;
  _unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public formBuilder: UntypedFormBuilder,
    public creditService: CreditService,
    public clientService: ClientService,
    public translate: TranslateService,
    public _viewContainerRef: ViewContainerRef,
    public _dialogService: DialogProviderService,
    private notificationService: NotificationService,
    private contactService: ContactService,
    private router: Router,
    @Optional()
    private dialogRef: MatDialogRef<EventLogAddContactDialogComponent>,
    private dialog: MatDialog,
    private identityReportsService: IdentityReportsService
  ) {
    this.currDate = new Date();
    this.contactList = [];
    this.setInteractionMode(InteractionMode.EDIT);
    this.modes = InteractionMode;
  }

  ngOnInit() {
    this.isInCreditEdit = this.router.url.includes('credits');
    this.isInClientEdit = this.router.url.includes('clients');

    // Global save in client handles the case of unsaved individual forms
    // this.contactService.saveRequested
    //   .pipe(takeUntil(this._unsubscribe))
    //   .subscribe(() => {
    //     this.contactForms.controls.forEach(
    //       (formGroup: UntypedFormGroup, index: number) => {
    //         if (this.saveBtnEnabled[index]) {
    //           this.onSaveContact(index);
    //         }
    //       }
    //     );
    //   });

    this.contactService.onContactAddedSubject
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((contact: ContactPhone) => {
        this.setInitialData();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.client && this.client && this.client.id) ||
      (changes.interactionMode && this.interactionMode === InteractionMode.NEW)
    ) {
      this.prevClientMobile = this.client.mobile;
      this.setInitialData();
    } else if (
      changes.client &&
      this.client &&
      !this.client.id &&
      this.interactionMode === InteractionMode.EDIT
    ) {
      this.getPhoneTypes();
    }

    if (
      this.interactionMode === InteractionMode.EDIT &&
      this.client &&
      this.client.id
    ) {
      this.contactService.contactsSubject
        .pipe(takeUntil(this._unsubscribe))
        .subscribe((contacts: ContactPhone[]) => {
          if (contacts && contacts.length > 0) {
            this.contactList = contacts;
            this.mapContactsToForm(this.contactList);
          }
        });
    }
  }

  // ngAfterViewInit(): void {
  //   if (this.phoneNumber && this.interactionMode === InteractionMode.NEW) {
  //     setTimeout(() => {
  //       this.focusDescriptionInput();
  //     }, 100); // Have to delay it or use AfterViewChecked
  //   }
  // }

  setInitialData() {
    if (this.interactionMode === this.modes.NEW) {
      this.getContactDataNewMode();
    }

    if (this.interactionMode === this.modes.EDIT) {
      this.getPhoneTypes();
      this.getContactList();
    }
  }

  setInteractionMode(interactionModeToSet: InteractionMode) {
    this.interactionMode = interactionModeToSet;
  }

  createExistingContactForm(contact: ContactPhone): UntypedFormGroup {
    const fb = this.formBuilder;
    return fb.group({
      id: contact.id,
      type: contact.type,
      phone: [
        contact.phone,
        [
          Validators.required,
          Validators.pattern(/^\+359[0-9]*$/),
          Validators.minLength(10),
          Validators.maxLength(13),
        ],
      ],
      description: [contact.description, Validators.maxLength(64)],
      active: contact.active,
      updatedAt: contact.updatedAt,
      updatedBy: contact.updatedBy,
    });
  }

  createNewContactForm(): UntypedFormGroup {
    const fb = this.formBuilder;
    return fb.group({
      id: null,
      type: [null, Validators.required],
      phone: [
        '',
        [
          Validators.required,
          Validators.pattern(/^\+359[0-9]*$/),
          Validators.minLength(10),
          Validators.maxLength(13),
        ],
      ],
      description: ['', Validators.maxLength(64)],
      active: false,
    });
  }

  getPhoneTypes() {
    if (this.client && this.client.id) {
      return this.contactService
        .getContactTypes(this.client.id)
        .pipe(takeUntil(this._unsubscribe))
        .subscribe((phoneTypes: ContactPhoneType[]) => {
          this.contactsPhoneType = phoneTypes;
        });
    }

    this.contactService.phoneTypesSubject
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((phoneTypes: ContactPhoneType[]) => {
        if (phoneTypes && phoneTypes.length > 0) {
          this.contactsPhoneType = phoneTypes;
        }
      });

    if (this.contactService.phoneTypesSubject.getValue()?.length === 0) {
      this.contactService
        .getContactTypes(0)
        .pipe(
          catchError(() => of(PHONE_TYPES)),
          takeUntil(this._unsubscribe)
        )
        .subscribe((phoneTypes: ContactPhoneType[]) => {
          if (!this.contactsPhoneType || this.contactsPhoneType.length === 0) {
            this.contactsPhoneType =
              phoneTypes && phoneTypes.length > 0 ? phoneTypes : PHONE_TYPES;
          }
        });
    }
  }

  getContactList() {
    return this.contactService
      .getContactList(this.client.id)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((contacts: ContactPhone[]) => {
        this.contactList = contacts.sort((a: ContactPhone, b: ContactPhone) => b.id - a.id);

        this.mapContactsToForm(this.contactList);
      });
  }

  mapContactsToForm(contactList: ContactPhone[]) {
    this.contactForms = this.formBuilder.array(
      contactList.map((contact, formIndex): UntypedFormGroup => {
        const formGroup = this.createExistingContactForm(contact);
        this.saveBtnEnabled[formIndex] = false;
        this.watchForFormChanges(formGroup);
        return formGroup;
      })
    );

    // this.contactForms.controls.sort((a, b) => {
    //   const aDate = a.get('updatedAt')?.value ?? '';
    //   const bDate = b.get('updatedAt')?.value ?? '';
    //   return bDate.localeCompare(aDate);
    // });
  }

  getContactDataNewMode() {
    this.contactsPhoneType = this.inputPhoneTypes;
    this.contactList = this.inputContacts;
    this.addNewContactFormGroup();
  }

  watchForFormChanges(form: UntypedFormGroup) {
    if (form['_valueChangesSubscription']) {
      form['_valueChangesSubscription'].unsubscribe();
    }

    const initialFormValue = cloneDeep(form.value);
    const subscription = form.valueChanges.pipe(takeUntil(this._unsubscribe)).subscribe(() => {
      const isExistingContact = form.get('id').value !== null;
      const formIndex = this.contactForms.controls.indexOf(form);
      const hasFormValueChanged = !isEqual(form.value, initialFormValue);

      this.saveBtnEnabled[formIndex] =
        (isExistingContact && hasFormValueChanged && form.valid) ||
        (!isExistingContact && form.valid);

      this.emitFormState();
    });

    form['_valueChangesSubscription'] = subscription;
  }

  comparePhoneTypes(
    phoneTypeOne: ContactPhoneType,
    phoneTypeTwo: ContactPhoneType
  ) {
    return phoneTypeOne.id === phoneTypeTwo.id ? true : false;
  }

  addNewContactFormGroup() {
    const newContactForm = this.createNewContactForm();
    this.watchForFormChanges(newContactForm);

    this.contactForms.insert(0, newContactForm);
    this.saveBtnEnabled.unshift(false);
  }

  replaceWithExistingContactForm(formIndex: number, newForm: UntypedFormGroup) {
    this.contactForms.setControl(formIndex, newForm);
    this.watchForFormChanges(newForm);
  }

  focusDescriptionInput() {
    if (this.descriptionInputRef && this.descriptionInputRef.nativeElement) {
      this.descriptionInputRef.nativeElement.focus();
    }
  }

  deleteContactFormGroup(formIndex: number) {
    const contactForm = this.contactForms.at(formIndex);

    const contactDto: ContactPhoneDto | ContactPhone = contactForm.value;
    const contactId = contactDto.id;

    this.translate
      .get(
        [
          'clients.deleteContact',
          'global.confirm',
          'global.cancel',
          'global.delete',
        ],
        {
          contact: contactDto.phone,
        }
      )
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((translation) => {
        const dialogConfig = {
          message: translation['clients.deleteContact'],
          disableClose: false,
          viewContainerRef: this._viewContainerRef,
          title: translation['global.delete'],
          cancelButton: translation['global.cancel'],
          acceptButton: translation['global.confirm'],
        };

        if (contactId) {
          this._dialogService
            .openConfirm(dialogConfig)
            .afterClosed()
            .pipe(takeUntil(this._unsubscribe))
            .subscribe((accept: boolean) => {
              if (accept) {
                this.contactService
                  .deleteContact(this.client.id, contactId)
                  .pipe(takeUntil(this._unsubscribe))
                  .subscribe(
                    (deletedContact) => {
                      this.contactForms.removeAt(formIndex);
                      this.saveBtnEnabled.splice(formIndex, 1);

                      this.notificationService.showLocalizedSuccessMessage({
                        notificationText:
                          'communication.contactSuccessfullyDeleted',
                      });

                      this.contactList = this.contactList.filter(
                        (contact: ContactPhone) => contact.id !== contactDto.id
                      );

                      this.contactService.contactsSubject.next(
                        this.contactList
                      );
                    },
                    (err: HttpErrorResponse) => {
                      console.error(err);
                      this.notificationService.showLocalizedErrorMessage({
                        notificationText: 'communication.contactDeleteError',
                      });
                    }
                  );
              }
            });
        } else {
          this.contactForms.removeAt(formIndex);
          this.saveBtnEnabled.splice(formIndex, 1);
        }
      });
  }

  onSaveContact(formIndex: number) {
    const contactForm = this.contactForms.at(formIndex);
    if (contactForm.valid) {
      const contactDto: ContactPhoneDto = contactForm.value;
      const contactId: number = contactDto.id;
      if (contactId) {
        // Existing contact. ID is a real number.
        this.contactService
          .editContact(this.client.id, contactId, contactDto)
          .pipe(takeUntil(this._unsubscribe))
          .subscribe(
            (updatedContact) => {
              this.saveBtnEnabled[formIndex] = false;

              const updatedContactForm =
                this.createExistingContactForm(updatedContact);

              this.replaceWithExistingContactForm(
                formIndex,
                updatedContactForm
              );

              this.updateContactList(updatedContact);
              this.notificationService.showLocalizedSuccessMessage({
                notificationText: 'communication.contactSuccessfullyUpdated',
              });
            },
            (err: HttpErrorResponse) => {
              console.error(err);
              this.notificationService.showLocalizedErrorMessage({
                notificationText: 'communication.contactEditError',
              });
            }
          );
      } else if (!contactId) {
        // New Contact. ID is null.
        this.contactService
          .createContact(this.client.id, contactDto)
          .pipe(takeUntil(this._unsubscribe))
          .subscribe(
            (newContact) => {
              this.saveBtnEnabled[formIndex] = false;
              const newForm = this.createExistingContactForm(newContact);
              this.replaceWithExistingContactForm(formIndex, newForm);

              this.updateContactList(newContact);
              if (this.interactionMode === this.modes.NEW) {
                this.dialogRef.close(true);
              }
              this.notificationService.showLocalizedSuccessMessage({
                notificationText: 'communication.contactSuccessfullyAdded',
              });
            },
            (err: HttpErrorResponse) => {
              console.error(err);
              this.notificationService.showLocalizedErrorMessage({
                notificationText: 'communication.contactAddError',
              });
            }
          );
      }
    } else {
      // Form is invalid
      this.notificationService.showLocalizedErrorMessage({
        notificationText: 'forms.formSavedIsInvalid',
      });
    }
  }

  updateContactList(updatedContact: ContactPhone) {
    const contactIndex = this.contactList.findIndex(
      (contact: ContactPhone) => contact.id === updatedContact.id
    );

    if (contactIndex !== -1) {
      this.contactList[contactIndex] = updatedContact;
    } else {
      this.contactList = [updatedContact, ...this.contactList];
    }

    this.contactService.contactsSubject.next(this.contactList);
  }

  emitFormState() {
    const hasAnyFormChanged = this.saveBtnEnabled.some((enabled: boolean) => enabled);
    const allFormsValid = this.contactForms.controls.every(
      (form: UntypedFormGroup) => form.valid
    );

    this.dataChanged.emit({
      valid: allFormsValid,
      data: this.contactForms.value,
      dirty: hasAnyFormChanged,
    });
  }

  openCompanyPhones() {
    this.identityReportsService
      .getCompanyPhones$(this.client.employerId)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((res) => {
        this.dialog.open(CompanyPhonesComponent, {
          minWidth: '1000px',
          minHeight: '560px',
          data: {
            identifier: this.client.employerId,
            clientId: this.client.id,
            phones: res,
            addContactType: this.contactsPhoneType.find(
              (type) => type.code === 'OTHER'
            ), // TODO add contact types enum
            contactListPhones: this.contactList.map((contact) => contact.phone),
          },
        });
      });
  }

  ngOnDestroy() {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
